import { ArrowLeftIcon, CopyIcon, DeviceMobileIcon, PencilSimpleLineIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { StatusBadge } from '@/components/cms/ui/status-badge';
import { PageTitle } from '@/components/ui/text';

type ProjectStatus = 'draft' | 'live' | 'archived' | 'pending' | 'locked' | 'template';

interface ProjectHeaderProps {
  title: string;
  status: ProjectStatus;
  onBack: () => void;
  onCopy: () => void;
  onTest: () => void;
  onEdit: () => void;
  onPublish: () => void;
  /** Copy shows only when the plan allows copying a project. */
  canCopy?: boolean;
  /** Test and Publish wait while the stories they list are being read. */
  busy?: boolean;
}

/** A project page's header bar: back to the dashboard, the project's name and status, and Copy, Test, Edit Project and Publish. */
export function ProjectHeader({ title, status, onBack, onCopy, onTest, onEdit, onPublish, canCopy = true, busy = false }: ProjectHeaderProps) {
  return (
    <div className="sticky top-0 z-lureo-layer-bar surface-bar">
      <div className="container py-4">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-1">
            <LureoButton
              data-tour="project-back"
              variant="no-outline"
              onClick={onBack}
              className="h-9 w-9 shrink-0 p-0 md:w-auto md:px-4">
              <ArrowLeftIcon className="h-5 w-5" />
              <span className="hidden md:inline">Dashboard</span>
            </LureoButton>
            <div className="hidden h-6 w-px shrink-0 bg-lureo-line md:block" />
            <div data-tour="project-title-and-status" className="ml-2 flex shrink-0 items-center gap-2">
              <PageTitle className="truncate">
                {title}
              </PageTitle>
              <StatusBadge status={status} />
            </div>
          </div>

          {/* Desktop buttons */}
          <div data-tour="project-share-actions" className="hidden items-center md:gap-3 lg:flex">
            {canCopy && (
              <LureoButton
                variant="outline-2"
                className="h-9 rounded-lureo-radius-button-pill px-3"
                onClick={onCopy}>
                <CopyIcon className="h-5 w-5" />
                Copy
              </LureoButton>
            )}
            <LureoButton
              variant="outline-2"
              className="h-9 rounded-lureo-radius-button-pill px-3"
              onClick={onTest}
              disabled={busy}
              tooltipContent={status === 'live' ? 'Share your project' : 'Test your project on your phone, no need to publish'}
              tooltipPosition="bottom">
              <DeviceMobileIcon className="h-5 w-5" />
              Test
            </LureoButton>
            <LureoButton className="h-9 rounded-lureo-radius-button-pill px-4" variant="outline-2" onClick={onEdit}>
              <PencilSimpleLineIcon className="h-5 w-5" />
              Edit Project
            </LureoButton>
            <LureoButton
              variant="secondary"
              className="h-9 rounded-lureo-radius-button-pill px-3"
              onClick={onPublish}
              disabled={busy}>
              <UploadSimpleIcon className="h-5 w-5" />
              Publish
            </LureoButton>
          </div>

          {/* Mobile/Tablet menu button */}
          <div className="lg:hidden">
            <LureoButton variant="outline-3" aria-label="Edit project" onClick={onEdit} className="h-9 w-9 rounded-full p-0">
              <PencilSimpleLineIcon className="h-5 w-5" />
            </LureoButton>
          </div>
        </div>
      </div>
    </div>
  );
}
